import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Trash2, Edit, RefreshCw, MoreVertical, X } from 'lucide-react';
import { getUsers, updateUserRole, deleteUser } from '../lib/user-service';
import type { UserProfile } from '@/types/user';
import { useAuth } from '../contexts/AuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

interface UserManagementProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}

const UserManagementV2 = ({ isOpen, onOpenChange }: UserManagementProps) => {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<UserProfile | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { user } = useAuth();

  useEffect(() => {
    if (isOpen) {
      loadUsers();
    }
  }, [isOpen]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const userList = await getUsers();
      setUsers(userList);
    } catch {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (userToEdit: UserProfile) => {
    setEditingUser(userToEdit);
    setShowEditDialog(true);
    setError('');
    setSuccess('');
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;

    try {
      await updateUserRole(editingUser.id, editingUser.role);
      setShowEditDialog(false);
      setEditingUser(null);
      setSuccess('User role updated successfully');
      setTimeout(() => setSuccess(''), 3000);
      await loadUsers();
    } catch {
      setError('Failed to update user role');
    }
  };

  const handleDeleteUser = async (userToDelete: UserProfile) => {
    if (userToDelete.id === user?.id) {
      setError('You cannot delete your own account');
      setTimeout(() => setError(''), 3000);
      return;
    }

    if (confirm(`Are you sure you want to delete ${userToDelete.email}?`)) {
      try {
        await deleteUser(userToDelete.id);
        setSuccess('User deleted successfully');
        setTimeout(() => setSuccess(''), 3000);
        await loadUsers();
      } catch {
        setError('Failed to delete user');
      }
    }
  };

  const renderRoleBadge = (role: 'admin' | 'reader' | 'guest') => {
    const roleStyles = {
      admin: 'bg-red-200/80 text-red-800 dark:bg-red-900/50 dark:text-red-200 border border-red-300/80 dark:border-red-800/80',
      reader: 'bg-blue-200/80 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200 border border-blue-300/80 dark:border-blue-800/80',
      guest: 'bg-gray-200/80 text-gray-800 dark:bg-gray-700/50 dark:text-gray-200 border border-gray-300/80 dark:border-gray-600/80',
    };
    return (
      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${roleStyles[role]}`}>
        {role.charAt(0).toUpperCase() + role.slice(1)}
      </span>
    );
  };

  const renderUserTable = () => (
    <div className="overflow-x-auto">
      <Table className="min-w-full">
        <TableHeader>
          <TableRow className="border-b border-gray-200/80 dark:border-gray-700/80">
            <TableHead className="text-gray-600 dark:text-gray-300">Email</TableHead>
            <TableHead className="text-gray-600 dark:text-gray-300">Role</TableHead>
            <TableHead className="text-gray-600 dark:text-gray-300">Joined</TableHead>
            <TableHead className="text-right text-gray-600 dark:text-gray-300">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((userProfile) => (
            <TableRow key={userProfile.id} className="border-b border-gray-200/50 dark:border-gray-700/50 hover:bg-gray-50/50 dark:hover:bg-gray-800/20">
              <TableCell className="font-medium text-gray-800 dark:text-gray-100">{userProfile.email}</TableCell>
              <TableCell>{renderRoleBadge(userProfile.role)}</TableCell>
              <TableCell className="text-gray-600 dark:text-gray-400">
                {userProfile.created_at ? new Date(userProfile.created_at).toLocaleDateString() : 'N/A'}
              </TableCell>
              <TableCell className="text-right">
                <Button variant="ghost" size="sm" onClick={() => handleEditUser(userProfile)} className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100">
                  <Edit className="h-4 w-4" />
                </Button>
                {userProfile.id !== user?.id && (
                  <Button variant="ghost" size="sm" onClick={() => handleDeleteUser(userProfile)} className="text-red-500 hover:text-red-700 dark:text-red-500/80 dark:hover:text-red-500">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );

  const renderUserCards = () => (
    <div className="space-y-3">
      {users.map((userProfile) => (
        <Card key={userProfile.id} className="bg-white/80 dark:bg-gray-800/50 backdrop-blur-sm border-gray-200/80 dark:border-gray-700/60 shadow-sm">
          <CardContent className="p-4 flex justify-between items-center">
            <div className="space-y-2">
              <div className="font-medium text-gray-800 dark:text-gray-100">{userProfile.email}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Joined: {userProfile.created_at ? new Date(userProfile.created_at).toLocaleDateString() : 'N/A'}
              </div>
              {renderRoleBadge(userProfile.role)}
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="text-gray-500 dark:text-gray-400">
                  <MoreVertical className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md border-gray-200/80 dark:border-gray-700/80">
                <DropdownMenuItem onClick={() => handleEditUser(userProfile)} className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700/80">
                  <Edit className="mr-2 h-4 w-4" />
                  <span>Edit Role</span>
                </DropdownMenuItem>
                {userProfile.id !== user?.id && (
                  <DropdownMenuItem onClick={() => handleDeleteUser(userProfile)} className="text-red-600 dark:text-red-500/90 cursor-pointer hover:!bg-red-50 dark:hover:!bg-red-900/50">
                    <Trash2 className="mr-2 h-4 w-4" />
                    <span>Delete</span>
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </CardContent>
        </Card>
      ))}
    </div>
  );

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-4xl p-0 bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl border-gray-200/50 dark:border-gray-700/50 rounded-2xl shadow-2xl">
          <div className="relative">
            <DialogClose asChild>
              <Button variant="ghost" size="icon" className="absolute top-3 right-3 z-20 h-9 w-9 rounded-full">
                <X className="h-5 w-5" />
              </Button>
            </DialogClose>
            <DialogHeader className="p-6 pb-4">
            <DialogTitle className="text-2xl font-bold text-gray-900 dark:text-gray-50">User Management</DialogTitle>
            <DialogDescription className="text-gray-600 dark:text-gray-400">
              Manage user roles and access. Users are added automatically when they sign up.
            </DialogDescription>
          </DialogHeader>
          <div className="p-6 pt-0">
            {error && <div className="bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-800/50 text-red-700 dark:text-red-200 px-4 py-3 rounded-lg mb-4 text-sm">{error}</div>}
            {success && <div className="bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-800/50 text-green-700 dark:text-green-200 px-4 py-3 rounded-lg mb-4 text-sm">{success}</div>}

            {loading ? (
              <div className="text-center p-8 text-gray-500 dark:text-gray-400">Loading users...</div>
            ) : (
              <>
                <div className="hidden sm:block">{renderUserTable()}</div>
                <div className="sm:hidden">{renderUserCards()}</div>
              </>
            )}
          </div>
          <DialogFooter className="p-6 pt-4 bg-gray-50/50 dark:bg-gray-800/20 rounded-b-2xl">
            <Button onClick={loadUsers} variant="outline" className="dark:bg-gray-700/50 dark:border-gray-600/80 dark:hover:bg-gray-700">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </DialogFooter>
        </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl border-gray-200/50 dark:border-gray-700/50 rounded-2xl shadow-2xl">
          <div className="relative">
            <DialogClose asChild>
              <Button variant="ghost" size="icon" className="absolute top-3 right-3 z-20 h-9 w-9 rounded-full">
                <X className="h-5 w-5" />
              </Button>
            </DialogClose>
            <DialogHeader>
            <DialogTitle className="text-xl font-bold text-gray-900 dark:text-gray-50">Edit User Role</DialogTitle>
          </DialogHeader>
          {editingUser && (
            <div className="space-y-6 py-4">
              <div>
                <Label className="text-gray-600 dark:text-gray-400">Email</Label>
                <div className="text-sm text-gray-800 dark:text-gray-200 mt-1">{editingUser.email}</div>
              </div>
              <div>
                <Label className="text-gray-600 dark:text-gray-400">Role</Label>
                <Select
                  value={editingUser.role}
                  onValueChange={(value: 'admin' | 'reader' | 'guest') =>
                    setEditingUser({ ...editingUser, role: value })
                  }
                >
                  <SelectTrigger className="mt-1 dark:bg-gray-800/70 dark:border-gray-700/80">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md border-gray-200/80 dark:border-gray-700/80">
                    <SelectItem value="guest" className="cursor-pointer">Guest - Limited read-only access</SelectItem>
                    <SelectItem value="reader" className="cursor-pointer">Reader - Can view and add recipes</SelectItem>
                    <SelectItem value="admin" className="cursor-pointer">Admin - Full access including user management</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)} className="dark:bg-gray-700/50 dark:border-gray-600/80 dark:hover:bg-gray-700">
              Cancel
            </Button>
            <Button onClick={handleUpdateUser} className="bg-purple-600 hover:bg-purple-700 text-white">Update Role</Button>
          </DialogFooter>
        </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UserManagementV2;
